import { useEffect, useMemo, useState } from "react";
import { useInboxLocalState } from "./inboxLocalState";

export type InboxTriage = {
  important?: boolean;
  settled?: boolean;
  snoozedUntil?: string | undefined;
};
export type InboxTriageView = "inbox" | "settled" | "snoozed";
export type InboxImportance = "all" | "important" | "ordinary";
export function parseInboxTriage(raw: string): Record<string, InboxTriage> {
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== "object" || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value).flatMap(([key, entry]) => {
        if (!entry || typeof entry !== "object" || Array.isArray(entry)) return [];
        return [
          [
            key,
            {
              important: entry.important === true,
              settled: entry.settled === true,
              ...(typeof entry.snoozedUntil === "string" &&
              Number.isFinite(Date.parse(entry.snoozedUntil))
                ? { snoozedUntil: entry.snoozedUntil }
                : {}),
            },
          ],
        ];
      }),
    );
  } catch {
    return {};
  }
}
export function matchesInboxTriage(
  state: InboxTriage,
  view: InboxTriageView,
  importance: InboxImportance,
  now: number,
) {
  const snoozed = !state.settled && Date.parse(state.snoozedUntil ?? "") > now;
  return (
    (view === "settled"
      ? !!state.settled
      : view === "snoozed"
        ? snoozed
        : !state.settled && !snoozed) &&
    (importance === "all" || (importance === "important" ? !!state.important : !state.important))
  );
}
export function updateInboxTriage(raw: string, key: string, patch: InboxTriage): string {
  const state = parseInboxTriage(raw);
  return JSON.stringify({ ...state, [key]: { ...state[key], ...patch } });
}
export function useInboxTriage(scope: string | null) {
  const { raw, update, available } = useInboxLocalState(scope ? `${scope}:triage:v1` : null);
  const states = useMemo(() => parseInboxTriage(raw), [raw]);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const refresh = () => setNow(Date.now());
    const next = Math.min(
      ...Object.values(states)
        .filter((s) => !s.settled)
        .map((s) => Date.parse(s.snoozedUntil ?? ""))
        .filter((time) => time > now),
    );
    const timer = Number.isFinite(next)
      ? window.setTimeout(refresh, Math.min(2_147_483_647, Math.max(0, next - Date.now())))
      : undefined;
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [states, now]);
  return {
    states,
    now,
    available,
    change: (key: string, patch: InboxTriage) =>
      update((raw) => updateInboxTriage(raw, key, patch)),
  };
}
